import React, { useState, useEffect } from "react";
import { Card, Button, Space, message, Row, Col, Statistic } from "antd";
import { PlusOutlined, TagOutlined, SettingOutlined } from "@ant-design/icons";
import UldTagForm from "./components/UldTagForm";
import UldTagTable from "./components/UldTagTable";
import { DEFAULT_TAG_PAGE_SIZE } from "./constants/tagConstants";
import { uldService } from "../../../api/enhancedApi";
import PermissionGuard from "../../../components/auth/PermissionGuard";

const UldTagManagementPage = () => {
    // 状态管理
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: DEFAULT_TAG_PAGE_SIZE,
        total: 0,
    });

    // 统计数据
    const [statistics, setStatistics] = useState({
        total: 0,
        systemTags: 0,
        customTags: 0,
    });

    // 获取标签列表
    const fetchTags = async () => {
        try {
            setLoading(true);
            const response = await uldService.listTagDefinitions();
            // 按照API_GUIDELINES规范处理响应格式：{success: true, message: "...", data: [...]}
            const tagList = response.data.data || [];

            setTags(tagList);
            setPagination((prev) => ({
                ...prev,
                total: tagList.length,
            }));

            // 计算统计数据
            const systemTags = tagList.filter((tag) => tag.is_system_tag).length;
            const customTags = tagList.filter((tag) => !tag.is_system_tag).length;
            setStatistics({
                total: tagList.length,
                systemTags,
                customTags,
            });
        } catch (error) {
            message.error("获取标签列表失败: " + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    // 组件加载时获取数据
    useEffect(() => {
        fetchTags();
    }, []);

    // 创建标签
    const handleCreate = () => {
        setEditingTag(null);
        setFormVisible(true);
    };

    // 编辑标签
    const handleEdit = (tag) => {
        setEditingTag(tag);
        setFormVisible(true);
    };

    // 删除标签
    const handleDelete = async (tagId) => {
        try {
            const response = await uldService.deleteTagDefinition(tagId);
            message.success(response.data.message);
            fetchTags();
        } catch (error) {
            message.error("删除标签失败: " + (error.response?.data?.detail || error.message));
        }
    };

    // 提交表单
    const handleFormSubmit = async (values) => {
        try {
            setFormLoading(true);

            if (editingTag) {
                // 编辑模式
                const response = await uldService.updateTagDefinition(editingTag.id, values);
                message.success(response.data.message);
            } else {
                // 创建模式
                const response = await uldService.createTagDefinition(values);
                message.success(response.data.message);
            }

            setFormVisible(false);
            setEditingTag(null);
            fetchTags();
        } catch (error) {
            message.error((editingTag ? "更新" : "创建") + "标签失败: " + (error.response?.data?.detail || error.message));
        } finally {
            setFormLoading(false);
        }
    };

    // 取消表单
    const handleFormCancel = () => {
        setFormVisible(false);
        setEditingTag(null);
    };

    // 表格分页/排序变化
    const handleTableChange = (pagination, filters, sorter) => {
        setPagination(pagination);
    };

    return (
        <div style={{ padding: "24px" }}>
            {/* 页面标题和操作 */}
            <div style={{ marginBottom: "24px" }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space align="center">
                            <TagOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
                            <h2 style={{ margin: 0 }}>ULD标签管理</h2>
                        </Space>
                    </Col>
                    <Col>
                        <PermissionGuard permission="uld:tag:manage">
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                                新建标签
                            </Button>
                        </PermissionGuard>
                    </Col>
                </Row>
            </div>

            {/* 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: "24px" }}>
                <Col span={8}>
                    <Card>
                        <Statistic title="标签总数" value={statistics.total} prefix={<TagOutlined />} valueStyle={{ color: "#1890ff" }} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title="系统标签" value={statistics.systemTags} prefix={<SettingOutlined />} valueStyle={{ color: "#fa8c16" }} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title="自定义标签" value={statistics.customTags} prefix={<TagOutlined />} valueStyle={{ color: "#52c41a" }} />
                    </Card>
                </Col>
            </Row>

            {/* 标签表格 */}
            <Card
                title="标签列表"
                style={{ minHeight: "500px" }}
                extra={
                    <Space>
                        <span style={{ color: "#666", fontSize: "14px" }}>共 {statistics.total} 个标签</span>
                    </Space>
                }
            >
                <UldTagTable tags={tags} loading={loading} onEdit={handleEdit} onDelete={handleDelete} pagination={pagination} onChange={handleTableChange} />
            </Card>

            {/* 标签表单对话框 */}
            <UldTagForm visible={formVisible} onCancel={handleFormCancel} onSubmit={handleFormSubmit} editingTag={editingTag} loading={formLoading} />
        </div>
    );
};

export default UldTagManagementPage;
